import { get, isNil } from "lodash";
import {
  ITEM_FLAGS,
  RECOMMENDED_POSITION,
} from "shared/components/ScreenRenderer/dataSources";
import { CARD_ENTITY, CARD_SOURCES, GUIDE_KINDS } from "./screen-groups";
import { registryFieldOf } from "./slot-editing";
import { BINDING_KINDS, SLOT_TYPES } from "./workshop-view";

export const KEEP = "keep";

const ACTION_PREFIX = "action:";

const VISIBLE_PREFIX = "visible:";

const CONDITIONAL_KIND = "conditional";

const VISIBLE_WHEN = "data-visible-when";

const VISIBLE_VALUE = "data-visible-value";

const FLAG_VALUE = "true";

const RECOMMENDED_FLAG = "recommended";

const ACTION_KINDS = [
  BINDING_KINDS.PROCESS_TRIGGER,
  BINDING_KINDS.SET_FIELD,
  BINDING_KINDS.CATALOG_ACTION,
];

export const collectionLabel = (name, labels) =>
  get(labels, ["COLLECTIONS", name]) || name;

const itemFieldLabel = ({ collection, field, labels, registry }) =>
  get(labels, ["ITEM_FIELDS", collection, field]) ||
  get(registryFieldOf(registry, `${collection}.${field}`), "label") ||
  field;

const cardFieldLabel = (field, labels) =>
  get(labels, ["CARD_FIELDS", field]) || field;

export const guideSources = ({ guide, collections, labels }) =>
  guide.kind === GUIDE_KINDS.LIST
    ? (collections || []).map((collection) => ({
        key: collection.name,
        label: collectionLabel(collection.name, labels),
        count: (collection.fields || []).length,
      }))
    : CARD_SOURCES.map((source) => ({
        key: source.key,
        label: get(labels, ["CARD_SOURCES", source.key]) || source.key,
        count: source.fields.length,
      }));

export const guideSourceOf = (guide) =>
  guide.kind === GUIDE_KINDS.LIST ? guide.collection : guide.source;

const isData = (slot) => get(slot, "binding.kind") === BINDING_KINDS.DATA;

const isConditional = (slot) => get(slot, "binding.kind") === CONDITIONAL_KIND;

const isFlag = (collection, field) =>
  get(ITEM_FLAGS, collection, []).includes(field);

const visibilityPatch = (path) => ({
  [VISIBLE_WHEN]: path,
  [VISIBLE_VALUE]: isNil(path) ? "" : FLAG_VALUE,
});

const visibleChoice = ({ slot, path, label, labels }) => ({
  key: `${VISIBLE_PREFIX}${path}`,
  label: labels.SHOWN_IF.replace("{{field}}", label),
  patch: {
    ...(isData(slot) ? { field: "" } : {}),
    attributes: visibilityPatch(path),
  },
});

const fieldPatch = (slot, path) => ({
  field: path,
  ...(isConditional(slot) ? { attributes: visibilityPatch(null) } : {}),
});

const hasAction = (slot) =>
  ACTION_KINDS.includes(get(slot, "binding.kind")) ||
  !isNil(get(slot, "binding.action"));

const keepLabel = (slot, labels) =>
  ({
    [SLOT_TYPES.IMAGE]: labels.KEEP_IMAGE,
    [SLOT_TYPES.INPUT]: labels.NO_SAVE,
  })[slot.type] || labels.KEEP_TEXT;

const actionChoices = ({ slot, actions, fieldLabels, labels }) => [
  ...(actions || []).map((action) => ({
    key: `${ACTION_PREFIX}${action.name}`,
    label:
      get(fieldLabels, ["TRIGGERS", action.name]) ||
      action.description ||
      action.name,
    patch: { action: action.name },
  })),
  {
    key: KEEP,
    label: labels.NO_ACTION,
    patch: hasAction(slot) ? { action: "" } : null,
  },
];

export const zoneChoices = ({
  guide,
  slot,
  labels,
  fieldLabels,
  collections,
  registry,
  actions,
}) => {
  const keep = {
    key: KEEP,
    label: keepLabel(slot, labels),
    patch:
      isData(slot) || isConditional(slot)
        ? {
            ...(isData(slot) ? { field: "" } : {}),
            ...(isConditional(slot)
              ? { attributes: visibilityPatch(null) }
              : {}),
          }
        : null,
  };
  if (guide.kind === GUIDE_KINDS.CARD) {
    const source = CARD_SOURCES.find((item) => item.key === guide.source);
    return [
      ...get(source, "fields", []).map((field) => ({
        key: `${CARD_ENTITY}.${field}`,
        label: cardFieldLabel(field, labels),
        patch: { field: `${CARD_ENTITY}.${field}` },
      })),
      keep,
    ];
  }
  if (slot.type === SLOT_TYPES.ACTION)
    return actionChoices({ slot, actions, fieldLabels, labels });
  const { collection } = guide;
  const fields = get(
    (collections || []).find((item) => item.name === collection),
    "fields",
    [],
  );
  return [
    ...fields.map((field) => {
      const path = `${collection}.${field}`;
      const label = itemFieldLabel({ collection, field, labels, registry });
      return isFlag(collection, field)
        ? visibleChoice({ slot, path, label, labels })
        : { key: path, label, patch: fieldPatch(slot, path) };
    }),
    keep,
  ];
};

export const canCenterRecommended = (collection) =>
  isFlag(collection, RECOMMENDED_FLAG);

export const recommendedCenterPatch = (on) => ({
  attributes: {
    [RECOMMENDED_POSITION.ATTRIBUTE]: on ? RECOMMENDED_POSITION.CENTER : "",
  },
});

export const zoneCurrent = (slot) => {
  const binding = get(slot, "binding") || {};
  if (slot.type === SLOT_TYPES.ACTION) {
    if (binding.kind === BINDING_KINDS.PROCESS_TRIGGER && binding.name)
      return `${ACTION_PREFIX}${binding.name}`;
    return hasAction(slot) ? null : KEEP;
  }
  if (isConditional(slot) && binding.field)
    return `${VISIBLE_PREFIX}${binding.field}`;
  if (isData(slot) && binding.source) return binding.source;
  if (binding.kind === BINDING_KINDS.UNBOUND) return null;
  return KEEP;
};

const visibleLabel = (slot, labels) => {
  const [entity, ...rest] = String(get(slot, "binding.field") || "").split(".");
  const field = rest.join(".");
  const value = get(slot, "binding.value");
  const name = get(labels, ["ITEM_FIELDS", entity, field]) || field || entity;
  return labels.SHOWN_IF.replace(
    "{{field}}",
    isNil(value) || value === FLAG_VALUE ? name : `${name} = ${value}`,
  );
};

export const zoneValue = ({ slot, choices, labels }) => {
  const current = zoneCurrent(slot);
  if (isNil(current)) return { text: labels.NOT_SET, set: false };
  if (current.startsWith(VISIBLE_PREFIX))
    return { text: visibleLabel(slot, labels), set: true };
  if (current === KEEP)
    return {
      text: slot.type === SLOT_TYPES.ACTION ? labels.NO_ACTION : labels.FIXED,
      set: true,
    };
  const choice = choices.find((item) => item.key === current);
  return { text: choice ? choice.label : current, set: true };
};

export const zoneChip = (slot, labels, fieldLabels) => {
  const current = zoneCurrent(slot);
  if (isNil(current) || current === KEEP) return null;
  if (current.startsWith(VISIBLE_PREFIX)) return visibleLabel(slot, labels);
  if (current.startsWith(ACTION_PREFIX)) {
    const name = current.slice(ACTION_PREFIX.length);
    return get(fieldLabels, ["TRIGGERS", name]) || name;
  }
  const [entity, ...rest] = current.split(".");
  const field = rest.join(".");
  if (entity === CARD_ENTITY) return cardFieldLabel(field, labels);
  return get(labels, ["ITEM_FIELDS", entity, field]) || field || current;
};
